import { type FC, useState, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';

import { addForceFavoriteRefetchHandler } from 'dibs-buyer-layout/exports/loginHandlers';
import { locations } from '../../../utils/tracking/shared/favoritesTracking';

import { FavoritesItem } from '../../../components/global/FavoriteIcons/FavoritesItem';
import { FavoritesProvider } from '../../../components/global/FavoriteIcons/FavoritesProvider';

import { type PdpSharedSidebarFavorites_user$key } from './__generated__/PdpSharedSidebarFavorites_user.graphql';
import { type PdpSharedSidebarFavorites_item$key } from './__generated__/PdpSharedSidebarFavorites_item.graphql';

import styles from './PdpSharedSidebarFavorites.scss';

export const PdpSharedSidebarFavorites: FC<{
    user?: PdpSharedSidebarFavorites_user$key | null;
    item: PdpSharedSidebarFavorites_item$key;
    isClient: boolean;
    userId?: string | null;
    showHeartCount?: boolean | null;
    itemImage?: string;
    wrapperClass?: string;
    heartIconWrapperClass?: string;
    folderIconWrapperClass?: string;
    isMobile?: boolean;
    isFolderFirst?: boolean;
    animateHeartPulse?: boolean;
    showNudge?: boolean;
    theme?:
        | 'dark'
        | 'light'
        | 'circle-light'
        | 'circle-frosted-small'
        | 'circle-frosted-medium'
        | 'circle-frosted-large';
}> = ({
    user: userRef,
    item: itemRef,
    isClient,
    userId,
    showHeartCount,
    itemImage,
    wrapperClass,
    heartIconWrapperClass,
    showNudge,
    folderIconWrapperClass,
    isMobile,
    isFolderFirst,
    animateHeartPulse = true,
    theme = 'dark',
}) => {
    const user = useFragment(
        graphql`
            fragment PdpSharedSidebarFavorites_user on User {
                hasInternalUserPermission: hasPermission(name: "INTERNAL_ADMIN_USER")
            }
        `,
        userRef
    );

    const item = useFragment(
        graphql`
            fragment PdpSharedSidebarFavorites_item on Item {
                ...FavoritesItem_item
                ...FavoritesProvider_item
            }
        `,
        itemRef
    );

    const [shouldFavoritesProviderRefetch, setShouldFavoritesProviderRefetch] = useState(false);
    const hasInternalUserPermission = !!user?.hasInternalUserPermission;

    useEffect(() => {
        addForceFavoriteRefetchHandler(() => {
            setShouldFavoritesProviderRefetch(true);
        });
    }, [setShouldFavoritesProviderRefetch]);

    return (
        <FavoritesProvider
            location={locations.SIDEBAR}
            disable={!isClient}
            userId={userId}
            items={[item]}
            shouldAutoFavorite
            shouldRefetch={shouldFavoritesProviderRefetch}
            shouldShowHeartCount={!!showHeartCount}
            fetchFolder={hasInternalUserPermission || !isMobile}
        >
            {({ props: renderProps }: $TSFixMe) => (
                <FavoritesItem
                    {...renderProps}
                    fetchFolder={renderProps?.fetchFolder}
                    item={item}
                    itemImage={itemImage}
                    wrapperClass={wrapperClass}
                    heartIconWrapperClass={heartIconWrapperClass}
                    folderIconWrapperClass={folderIconWrapperClass}
                    animateHeartPulse={animateHeartPulse}
                    heartIconClass={styles.icon}
                    folderIconClass={styles.icon}
                    theme={theme}
                    isFolderFirst={isFolderFirst}
                    showNudge={showNudge}
                />
            )}
        </FavoritesProvider>
    );
};

/**
 * Knip should ignore this duplicated export because it is lazy loaded
 * @alias
 */
export default PdpSharedSidebarFavorites;
